
/**
 * AuditAssignment Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';
import { UPLOAD_FILE_TIMEOUT_MS } from '@repo/contracts';
import { resolveOnUploadProgress, uploadAxiosConfig } from '../utils/upload-axios-config.js';

export class AuditAssignmentService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'AuditAssignment listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch audit-assignment list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch audit-assignment');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'AuditAssignment oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create audit-assignment');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'AuditAssignment güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update audit-assignment');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'AuditAssignment silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete audit-assignment');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: getMyAudits
     */
    async getMyAudits(params = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/my-audits`, { params });
            return {
                success: true,
                data: response.data?.data || response.data,
                pagination: response.data?.pagination,
                message: response.data?.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute getMyAudits');
        }
    }

    /**
     * Custom operation: createAccept
     */
    async createAccept(id, data = {}) {
        try {
            const response = await this.api.client.post(`/${id}/accept`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createAccept');
        }
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Upload evidence file for an assignment
     * @param {string} assignmentId
     * @param {File} file - browser File object
     * @param {Function|{ onUploadProgress?: Function }} [optionsOrCallback]
     */
    async uploadEvidence(assignmentId, file, optionsOrCallback) {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await this.api.client.post(
                `${this.api.basePath}/${assignmentId}/evidence`,
                formData,
                uploadAxiosConfig({
                    timeout: UPLOAD_FILE_TIMEOUT_MS,
                    onUploadProgress: resolveOnUploadProgress(optionsOrCallback),
                    headers: { 'Content-Type': 'multipart/form-data' },
                }),
            );
            return {
                success: true,
                data: response.data?.data || response.data,
                message: response.data?.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to upload evidence');
        }
    }

    /**
     * Delete evidence file
     * @param {string} assignmentId
     * @param {string} storedName - the server-side filename
     */
    async deleteEvidence(assignmentId, storedName) {
        try {
            const response = await this.api.client.delete(
                `${this.api.basePath}/${assignmentId}/evidence/${storedName}`
            );
            return {
                success: true,
                message: response.data?.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete evidence');
        }
    }

    /**
     * Download evidence file (returns blob URL)
     * @param {string} assignmentId
     * @param {string} storedName
     */
    async downloadEvidence(assignmentId, storedName) {
        try {
            const response = await this.api.client.get(
                `${this.api.basePath}/${assignmentId}/evidence/${storedName}`,
                { responseType: 'blob' }
            );
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to download evidence');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
